import { accountingUrl, payrollUrl } from 'src/environments/environment';

/**
 * Sends the app's own failures to the accounting server (a_error_reports) so
 * they can be triaged without waiting for a user to complain.
 *
 * Plain functions on purpose, no Angular injection: it has to work for a
 * failure that happens before Angular has started (a missing chunk after a
 * deploy, a broken bundle) and from inside the ErrorHandler itself.
 *
 * It uses fetch, not HttpClient, so a failing report can never be caught by the
 * interceptors and reported again. It never throws and never blocks the UI.
 *
 * Nothing about money goes in. Callers pass a short `extra` of ids and states;
 * the server also strips anything keyed like an amount, bank number or token.
 */

export type ErrorSource = 'frontend' | 'boot' | 'invariant';

export interface ErrorReport {
  source?: ErrorSource;
  kind?: string;
  message: string;
  stack?: string;
  httpMethod?: string;
  httpStatus?: number;
  extra?: Record<string, unknown>;
}

const MAX_PER_SESSION = 25;
const SAME_ERROR_GAP_MS = 10_000;
const BREADCRUMBS = 15;

let sent = 0;
const lastSent = new Map<string, number>();
const breadcrumbs: string[] = [];
const ctx: { organisationId?: string; userId?: string } = {};

/** Called once the signed-in user / active company are known. */
export function setErrorContext(c: { organisationId?: string | number | null; userId?: string | number | null }): void {
  if (c.organisationId != null) ctx.organisationId = String(c.organisationId);
  if (c.userId != null) ctx.userId = String(c.userId);
}

/** The last few things the user did, so a report says how to get there. */
export function addBreadcrumb(text: string): void {
  breadcrumbs.push(`${new Date().toISOString().slice(11, 19)} ${String(text).slice(0, 120)}`);
  if (breadcrumbs.length > BREADCRUMBS) breadcrumbs.shift();
}

export function isChunkLoadFailure(message: string): boolean {
  return /ChunkLoadError|Loading chunk [\w-]+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module/i
    .test(message || '');
}

export function reportError(report: ErrorReport): void {
  try {
    if (!report.message && !report.stack) return;
    if (sent >= MAX_PER_SESSION) return;
    const key = `${report.kind}|${report.message}`.slice(0, 200);
    const now = Date.now();
    if (now - (lastSent.get(key) || 0) < SAME_ERROR_GAP_MS) return;
    lastSent.set(key, now);
    sent += 1;

    const body = JSON.stringify({
      source: report.source || 'frontend',
      kind: report.kind || 'exception',
      message: report.message,
      stack: report.stack,
      httpMethod: report.httpMethod,
      httpStatus: report.httpStatus,
      route: location.pathname,
      // The server decides from the request's Origin; this is only the fallback.
      environment: /^(localhost|127\.|\[::1\]|192\.168\.|10\.|.*\.local$)/i.test(location.hostname) ? 'local' : 'deployed',
      organisationId: ctx.organisationId,
      userId: ctx.userId,
      appVersion: (window as any).__appVersion || undefined,
      breadcrumbs: breadcrumbs.slice(),
      extra: report.extra
    });
    post({ url: endpointFor(location.pathname), body });
  } catch { /* same */ }
}

/** Errors on payroll pages are kept by the payroll backend (it has the payroll code to diagnose them). */
function endpointFor(pathname: string): string {
  return pathname.startsWith('/payroll')
    ? `${payrollUrl}/error-reports`
    : `${accountingUrl}/accounting/error-reports`;
}

const QUEUE_KEY = 'ALTOMATE_ERROR_REPORT_QUEUE';
const QUEUE_MAX = 20;

type Queued = { url: string; body: string };

function readQueue(): Queued[] {
  try {
    const raw = JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]');
    // Entries saved by an earlier version are plain strings: those were accounting reports.
    return raw.map((q: string | Queued) => typeof q === 'string' ? { url: endpointFor('/'), body: q } : q);
  } catch { return []; }
}

function writeQueue(q: Queued[]): void {
  try {
    if (q.length) localStorage.setItem(QUEUE_KEY, JSON.stringify(q.slice(-QUEUE_MAX)));
    else localStorage.removeItem(QUEUE_KEY);
  } catch { /* storage blocked: the report is simply lost */ }
}

/**
 * A report about the server being down cannot reach the server. A report that
 * did not get through is kept here and resent on the next load, so the outage
 * itself is still recorded once the server answers again.
 */
function post(item: Queued): void {
  fetch(item.url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: item.body,
    keepalive: true
  }).then((r) => {
    if (!r.ok && r.status >= 500) throw new Error('not stored');
    flushQueue();
  }).catch(() => writeQueue([...readQueue(), item]));
}

let flushing = false;
export function flushQueue(): void {
  const q = readQueue();
  if (!q.length || flushing) return;
  flushing = true;
  writeQueue([]);
  Promise.all(q.map((item) => fetch(item.url, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: item.body
  }).then((r) => { if (!r.ok && r.status >= 500) throw new Error('not stored'); })
    .catch(() => writeQueue([...readQueue(), item]))))
    .finally(() => { flushing = false; });
}

// Resend whatever an earlier visit could not deliver.
setTimeout(flushQueue, 5000);

/** For a catch block that shows a toast or does nothing: say it happened. */
export function reportSwallowed(where: string, err: unknown, extra?: Record<string, unknown>): void {
  const e = err as any;
  reportError({
    kind: 'swallowed',
    message: `${where}: ${e?.error?.message || e?.message || String(err)}`,
    stack: e?.stack,
    httpStatus: typeof e?.status === 'number' ? e.status : undefined,
    extra
  });
}

/**
 * Accounting must balance. Call this where a screen has the lines in hand;
 * a difference is reported (once per distinct situation), never blocked here —
 * the server still decides whether the entry is accepted.
 */
export function checkBalanced(where: string, lines: Array<{ debit?: number | string; credit?: number | string }>, extra?: Record<string, unknown>): boolean {
  let d = 0;
  let c = 0;
  for (const l of lines || []) {
    d += Math.round((Number(l?.debit) || 0) * 100);
    c += Math.round((Number(l?.credit) || 0) * 100);
  }
  if (d === c) return true;
  reportError({
    source: 'invariant',
    kind: 'unbalanced',
    message: `${where}: debits and credits differ`,
    extra: { lines: (lines || []).length, differenceCents: d - c, ...extra }
  });
  return false;
}
